import * as React from 'react';
import { ErrorLogComponent } from '@cian/error-log-component';
import { VisitedOffersProvider, IVisitedOffersProviderProps } from '@cian/frontend-visited-offers-widget';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';
import { UiKitToastsContextProvider } from '@cian/ui-kit/context';
import { UiKitRoot } from '@cian/ui-kit/root';
import { uaToDeviceType } from '@cian/ui-kit/utils';
import { Provider } from 'react-redux';

import { AppEffects } from './AppEffects';
import { BannersRow } from '../../components/BannersRow';
import { ContentWrapper } from '../../components/ContentWrapper';
import { GenplanNavContainer } from '../../components/Genplan/components/GenplanNavContainer';
import { Main } from '../../components/Main';
import { OrderWrapper } from '../../components/OrderWrapper';
import { IApplicationContext } from '../../types/applicationContext';
import { ICustomContextParams } from '../../types/ICustomContextParams';
import { TReduxStore } from '../../types/redux';
import { Context as ApplicationContext } from '../../utils/withApplicationContext';
import { About } from '../About';
import { AddressContainer } from '../Address';
import { AdvantagesContainer } from '../Advantages';
import { AeroPhotoTourEntryContainer } from '../AeroPhotoTourEntry';
import { AeroPhotoTourModalContainer } from '../AeroPhotoTourModal';
import { AgentsContainer } from '../Agents';
import { BasicInfoContainer } from '../BasicInfo';
import { BreadCrumbs } from '../BreadCrumbs/BreadCrumbs';
import { BuildersContainer } from '../Builder';
import { BuilderCallReasonsContainer } from '../BuilderCallReasons';
import { BuilderChatPopupContainer } from '../BuilderChatPopup';
import { BuilderNewbuildingsContainer } from '../BuilderNewbuildings';
import { CarouselContainer } from '../Carousel';
import { CompanySeoCard } from '../CompanySeoCard';
import { ComparisonContainer } from '../Comparison';
import { ConstructionProgressContainer } from '../ConstructionProgress';
import { ConsultantChatPopupContainer } from '../ConsultantChatPopupContainer';
import { EcologyContainer } from '../Ecology';
import { EscrowContainer } from '../Escrow';
import { FixedHeader } from '../FixedHeader';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { FloorPlanLoadable } from '../FloorPlan/FloorPlanLoadable';
import { GenplanLoadable } from '../Genplan/GenplanLoadable';
import { HousesByTurnSeoContainer } from '../HousesByTurnSeo';
import { ImageSources } from '../ImageSources';
import { InteriorDecorationContainer } from '../InteriorDecoration';
import { LastHopePopupContainer } from '../LastHopePopup';
import { MapSelectorContainer } from '../MapSelector';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { MortgageMiniCalculatorWidget } from '../MortgageMiniCalculatorWidget';
import { MortgageSectionContainer } from '../MortgageSection';
import { NewbuildingCallbackWidgetProvider } from '../NewbuildingCallbackWidgetProvider';
import { NewbuildingBrokerModalsProvider } from '../NewbuildingBrokerModalsProvider';
import {
  NewbuildingPurchaseGiftBannerContainer,
  NewbuildingPurchaseGiftPromoProvider,
} from '../NewbuildingPurchaseGiftPromo';
import { NewbuildingReviewsTeaserContainer } from '../NewbuildingReviewsTeaser';
import { OffersBlockContainer } from '../OffersBlock';
import { OffersStatsContainer } from '../OffersStats';
import { Promo } from '../Promo';
import { PromoDetailsContainer } from '../PromoDetails';
import { PromosSectionContainer } from '../PromosSection';
import { RealtyValuationContainer } from '../RealtyValuation';
import { RecommendedNewbuildingsContainer } from '../RecommendedNewbuildings';
import { ReliabilityV2Container } from '../ReliabilityV2';
import { ScrollDown } from '../ScrollDown';
import { SeoMarkedListContainer } from '../SeoMarkedList';
import { SpecificationsContainer } from '../Specifications';
import { SpecificationsDetailedSeoContainer } from '../SpecificationsDetailedSeo';
import { TopControlsContainer } from '../TopControls';
import { Video } from '../Video';
import { VisitedOffersUpdater } from '../VisitedOffersUpdater';

export interface IApplicationProps extends IApplicationContext {
  reduxStore: TReduxStore;
  customContext: ICustomContextParams;
  /**
   * Заглушка корневого error boundary. В браузере это снимок SSR-разметки,
   * чтобы при ошибке после гидрации контент не стирался; на сервере не передаётся.
   */
  errorStub?: React.ReactElement;
}

export const Application = ({
  config,
  customContext,
  httpApi,
  logger,
  reduxStore,
  microfrontendsRegistry,
  telemetry,
  eventQueue,
  errorStub,
}: IApplicationProps) => {
  const context = React.useMemo(
    () => ({ config, httpApi, logger, microfrontendsRegistry, telemetry, eventQueue }),
    [config, httpApi, logger, microfrontendsRegistry, telemetry, eventQueue],
  );

  const { settings, enabled } = config.get<IVisitedOffersProviderProps>('audience.visitedOffers') || {};

  return (
    <ErrorLogComponent logger={logger} errorStub={errorStub}>
      <ApplicationContext.Provider value={context}>
        <UiKitRoot deviceType={uaToDeviceType(customContext.userAgent)}>
          <UiKitToastsContextProvider>
            <Provider store={reduxStore}>
              <MicrofrontendProvider registry={microfrontendsRegistry}>
                <NewbuildingBrokerModalsProvider>
                  <NewbuildingPurchaseGiftPromoProvider>
                    <NewbuildingCallbackWidgetProvider>
                      <VisitedOffersProvider settings={settings} enabled={enabled}>
                        <ErrorLogComponent logger={logger}>
                          <VisitedOffersUpdater />
                        </ErrorLogComponent>
                        <FixedHeader />
                        <Main>
                          <CarouselContainer />
                          <BreadCrumbs />
                          <FlatViewOrderTooltipContainer />
                          <TopControlsContainer />
                          <BasicInfoContainer />
                          <ScrollDown />
                        </Main>
                        <ContentWrapper>
                          <Promo />
                          <NewbuildingPurchaseGiftBannerContainer />
                          <AddressContainer />
                          <SpecificationsContainer />
                          <SpecificationsDetailedSeoContainer />
                          <BannersRow>
                            <BuilderCallReasonsContainer />
                            <ComparisonContainer />
                          </BannersRow>
                          <HousesByTurnSeoContainer />
                          <ErrorLogComponent logger={logger}>
                            <GenplanNavContainer>
                              <GenplanLoadable />
                            </GenplanNavContainer>
                            <FloorPlanLoadable />
                          </ErrorLogComponent>
                          <OrderWrapper order={1}>
                            <About />
                          </OrderWrapper>
                          <OrderWrapper order={0}>
                            <OffersBlockContainer />
                            <PromosSectionContainer />
                            <ErrorLogComponent logger={logger}>
                              <MortgageMiniCalculatorWidget />
                            </ErrorLogComponent>
                            <ErrorLogComponent logger={logger}>
                              <MortgageSectionContainer />
                            </ErrorLogComponent>
                            <MortgageBrokerBannerContainer />
                            <ErrorLogComponent logger={logger}>
                              <RealtyValuationContainer />
                            </ErrorLogComponent>
                          </OrderWrapper>
                          <OrderWrapper order={2}>
                            <Video />
                            <AdvantagesContainer />
                            <NewbuildingReviewsTeaserContainer />
                            <ErrorLogComponent logger={logger}>
                              <MapSelectorContainer />
                            </ErrorLogComponent>
                            <AeroPhotoTourEntryContainer />
                            <EcologyContainer />
                            <InteriorDecorationContainer />
                            <BuildersContainer />
                            <AgentsContainer />
                            <BuilderNewbuildingsContainer />
                            <ConstructionProgressContainer />
                            <ErrorLogComponent logger={logger}>
                              <ReliabilityV2Container />
                            </ErrorLogComponent>
                            <EscrowContainer />
                            <OffersStatsContainer />
                            <ErrorLogComponent logger={logger}>
                              <RecommendedNewbuildingsContainer />
                            </ErrorLogComponent>
                            <ImageSources />
                            <CompanySeoCard />
                            <SeoMarkedListContainer />

                            {/* Попапы. */}
                            <ErrorLogComponent logger={logger}>
                              <BuilderChatPopupContainer />
                              <ConsultantChatPopupContainer />
                              <LastHopePopupContainer />
                              <PromoDetailsContainer />
                              <AeroPhotoTourModalContainer />
                            </ErrorLogComponent>
                          </OrderWrapper>
                        </ContentWrapper>
                      </VisitedOffersProvider>
                    </NewbuildingCallbackWidgetProvider>
                  </NewbuildingPurchaseGiftPromoProvider>
                </NewbuildingBrokerModalsProvider>
                <ErrorLogComponent logger={logger}>
                  <AppEffects config={config} />
                </ErrorLogComponent>
              </MicrofrontendProvider>
            </Provider>
          </UiKitToastsContextProvider>
        </UiKitRoot>
      </ApplicationContext.Provider>
    </ErrorLogComponent>
  );
};
